import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { InterviewRecordAPI } from '../services/interviewRecordApi';

// Styled Components - 基于用户提供的示例样式
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  min-height: 100vh;
  padding: 40px 20px;
`;

const InterviewReport = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: #0f1220; /* 深色卡片 */
  border: 1px solid rgba(148,163,184,0.15);
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(2,6,23,0.45);
  overflow: hidden;
`;

const ReportHeader = styled.div`
  background: linear-gradient(135deg, #60a5fa, #8b5cf6);
  color: #fff;
  padding: 30px 25px;
  text-align: center;
  position: relative;
`;

const ReportTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ReportSubtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
`;

 

const ReportContent = styled.div`
  padding: 30px 25px;
  position: relative;
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: rgba(99, 102, 241, 0.14);
  border: none;
  border-radius: 6px;
  color: #000000;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: rgba(99, 102, 241, 0.24);
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #60a5fa;
  margin: 25px 0 12px;
  position: relative;
  padding-left: 20px;
  line-height: 1.4;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22d3ee, #8b5cf6);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding-left: 18px;

    &::before {
      width: 6px;
      height: 6px;
    }
  }
`;

const ScoreCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
`;

const ScoreItem = styled.div`
  flex: 1;
  min-width: 200px;
  background: #12162a;
  border: 1px solid rgba(148,163,184,0.15);
  border-radius: 10px;
  padding: 18px;
  box-shadow: 0 6px 18px rgba(2,6,23,0.35);

  strong {
    display: block;
    font-size: 24px;
    background: linear-gradient(135deg, #22d3ee, #60a5fa);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin-bottom: 6px;
  }

  span {
    font-size: 14px;
    color: #9aa6bf;
  }
`;

const ListGroup = styled.ul`
  list-style: none;
  padding-left: 20px;
  margin-top: 10px;

  li {
    position: relative;
    padding-left: 12px;
    margin: 8px 0;
    line-height: 1.6;
    color: #e5e7eb;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #60a5fa;
    }
  }
`;

const ImproveTips = styled.div`
  background: #12162a;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(148,163,184,0.15);
  margin-top: 15px;
  box-shadow: 0 6px 18px rgba(2,6,23,0.35);

  p {
    line-height: 1.8;
    color: #e5e7eb;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const OverallEvaluation = styled.p`
  line-height: 1.8;
  color: #e5e7eb;
`;

// eslint-disable-next-line no-unused-vars
const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #dc2626;
  gap: 16px;
`;

const InterviewReportPage = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        
        // 优先从后端获取面试记录
        let record = null;
        try {
          const res = await InterviewRecordAPI.getInterviewRecordRemote(interviewId);
          if (res.success) record = res.data;
        } catch {}
        if (!record) {
          const records = InterviewRecordAPI.getInterviewRecords();
          record = records.find(r => r.id === parseInt(interviewId));
        }
        
        if (!record) {
          throw new Error('面试记录不存在');
        }

        // 如果有评估数据，直接使用（兼容后端snake_case）
        const evaluation = record.evaluationData || record.evaluation_data;
        if (evaluation) {
          setReportData({
            ...record,
            ...evaluation
          });
        } else {
          // 否则使用模拟数据（实际项目中应该调用API重新评估）
          const mockEvaluationData = {
            score: record.score || 65,
            level: record.score >= 90 ? '优秀' : record.score >= 75 ? '良好' : record.score >= 60 ? '一般' : '需提升',
            overall_evaluation: '候选人具备一定的产品经验和数据驱动意识，尤其在推动淘宝直播后台产品从0到1的过程中展示了初步的项目管理和协调能力。然而，在回答过程中语言表达不够清晰，逻辑结构不强，未能完整阐述推动项目落地的具体方法和遇到的挑战，导致问题解决能力和沟通表达能力评分偏低。此外，面对追问时表现紧张，应变能力较弱，整体表现尚不足以充分展示其胜任高级产品经理岗位的能力。',
            strengths: [
              '具备从0到1建设产品经验（如淘宝直播后台）',
              '有一定的数据驱动意识（如提升用户留存率35%）',
              '具备跨部门协作和团队管理的初步能力'
            ],
            weaknesses: [
              '语言表达不清晰，逻辑性弱，未能有效传达项目细节',
              '回答问题缺乏结构化思维，未能围绕STAR模型展开描述',
              '面对追问反应较慢，应变能力和自信心不足',
              '未能深入阐述推动项目过程中的关键节点和挑战'
            ],
            recommendations: [
              '加强结构化表达训练，使用STAR法则（情境、任务、行动、结果）清晰描述项目经验',
              '准备常见产品面试问题的标准答案模板，提升语言流畅度和逻辑性',
              '模拟压力面试场景，提升临场应变能力和表达自信',
              '深入复盘过往项目，提炼关键成果与挑战，增强问题回答的深度和说服力'
            ]
          };

          setReportData({
            ...record,
            ...mockEvaluationData
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [interviewId]);

  

  const handleBack = () => {
    navigate('/app/interviews/mock');
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <div>加载报告失败: {error}</div>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={16} />
            返回
          </BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  if (!reportData) {
    return (
      <Container>
        <ErrorContainer>
          <div>未找到面试报告数据</div>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={16} />
            返回
          </BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      {/* 返回按钮 - 放在页面左上角 */}
      <BackButtonContainer>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={14} />
          返回
        </BackButton>
      </BackButtonContainer>

      <InterviewReport>
        {/* 顶部标题区 */}
        <ReportHeader>
          <ReportTitle>面试评估报告</ReportTitle>
          <ReportSubtitle>AI驱动智能求职助手 | 为你的职业发展精准赋能</ReportSubtitle>
        </ReportHeader>

        {/* 内容区 */}
        <ReportContent>
          {/* 分数与等级 */}
          <ScoreCard>
            <ScoreItem>
              <strong>{reportData.score}</strong>
              <span>综合得分</span>
            </ScoreItem>
            <ScoreItem>
              <strong>{reportData.level}</strong>
              <span>评定等级</span>
            </ScoreItem>
          </ScoreCard>

          {/* 总体评价 */}
          <div>
            <SectionTitle className="section-title">总体评价</SectionTitle>
            <OverallEvaluation>
              {reportData.overall_evaluation}
            </OverallEvaluation>
          </div>

          {/* 优势 */}
          <div>
            <SectionTitle className="section-title">优势亮点</SectionTitle>
            <ListGroup>
              {reportData.strengths?.map((strength, index) => (
                <li key={index}>{strength}</li>
              ))}
            </ListGroup>
          </div>

          {/* 不足 */}
          <div>
            <SectionTitle className="section-title">待改进项</SectionTitle>
            <ListGroup>
              {reportData.weaknesses?.map((weakness, index) => (
                <li key={index}>{weakness}</li>
              ))}
            </ListGroup>
          </div>

          {/* 改进建议 */}
          <div>
            <SectionTitle className="section-title">提升建议</SectionTitle>
            <ImproveTips>
              {reportData.recommendations?.map((recommendation, index) => (
                <p key={index}>{index + 1}. {recommendation}</p>
              ))}
            </ImproveTips>
          </div>
        </ReportContent>
      </InterviewReport>

      
    </Container>
  );
};

export default InterviewReportPage;
